import { useMemo, useState } from "react";
import { Can, useCan } from "@/auth/permissions";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { vehicleOpsApi, type MaintenanceOrder } from "@/services/vehicleOps";
import { listVehicles } from "@/services/vehicles";
import { PageShell, LoadingState } from "@/shared/ui/PageHeader";
import { Card, MetricsRow } from "@/shared/ui/Card";
import { VehicleOpsTabs } from "./VehicleOpsTabs";

const usd = (c: number | null | undefined) => "$" + ((c ?? 0) / 100).toLocaleString("en-US");

const TYPE_META: Record<string, { label: string; cls: string }> = {
  scheduled: { label: "保养", cls: "bg-blue-50 text-blue-700" },
  breakdown: { label: "故障", cls: "bg-amber-50 text-amber-700" },
  accident: { label: "事故", cls: "bg-rose-50 text-rose-700" },
  inspection: { label: "年检", cls: "bg-slate-100 text-slate-600" },
  recall: { label: "召回", cls: "bg-purple-50 text-purple-700" },
  other: { label: "其他", cls: "bg-slate-100 text-slate-600" },
};
const STATUS_META: Record<string, { label: string; cls: string }> = {
  awaiting_approval: { label: "待审", cls: "bg-amber-100 text-amber-700" },
  scheduled: { label: "待审", cls: "bg-amber-100 text-amber-700" },
  in_progress: { label: "维修中", cls: "bg-blue-100 text-blue-700" },
  completed: { label: "已完成", cls: "bg-emerald-100 text-emerald-700" },
  cancelled: { label: "已取消/驳回", cls: "bg-slate-100 text-slate-500" },
};
const STATUS_TABS = [
  { key: "all", label: "全部" },
  { key: "awaiting_approval", label: "待审" },
  { key: "in_progress", label: "维修中" },
  { key: "completed", label: "已完成" },
];

export function VehicleOpsMaintenance() {
  const canApproveMaint = useCan("vehicles.maintenance.approve");
  const canUpdateMaint = useCan("vehicles.maintenance.update");
  const qc = useQueryClient();
  const [tab, setTab] = useState("all");
  const [veh, setVeh] = useState("");
  const [selected, setSelected] = useState<MaintenanceOrder | null>(null);

  const ordersQ = useQuery({ queryKey: ["vehicle-ops", "maintenance"], queryFn: () => vehicleOpsApi.listMaintenance() });
  const vehQ = useQuery({ queryKey: ["vehicles", "all"], queryFn: () => listVehicles() });

  const vehMap = useMemo(() => {
    const m = new Map<string, { number: string; model: string }>();
    for (const v of vehQ.data ?? []) m.set(v.id, { number: v.vehicle_number, model: v.model_label_cn });
    return m;
  }, [vehQ.data]);

  const approve = useMutation({
    mutationFn: (id: string) => vehicleOpsApi.approveMaintenance(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["vehicle-ops"] }),
    onError: (e) => alert((e as Error).message || "审批失败"),
  });
  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => vehicleOpsApi.rejectMaintenance(id, reason),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["vehicle-ops"] }); setSelected(null); },
    onError: (e) => alert((e as Error).message || "驳回失败"),
  });
  const complete = useMutation({
    mutationFn: ({ id, cost }: { id: string; cost: number }) => vehicleOpsApi.completeMaintenance(id, cost),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["vehicle-ops"] }); setSelected(null); },
    onError: (e) => alert((e as Error).message || "完成失败"),
  });

  if (ordersQ.isLoading) return <LoadingState />;
  if (ordersQ.isError) return (
    <div className="flex flex-col items-center gap-2 py-16 text-[13px] text-slate-500">
      <span>维修工单加载失败</span>
      <button onClick={() => ordersQ.refetch()} className="rounded-md border border-slate-300 px-3 py-1 text-[12px] font-medium text-blue-600 hover:bg-blue-50">重试</button>
    </div>
  );
  if (!ordersQ.data) return <LoadingState />;
  const orders = ordersQ.data;

  const thisMonth = new Date().toISOString().slice(0, 7);
  const pending = orders.filter((o) => o.status === "awaiting_approval");
  const inRepair = orders.filter((o) => o.status === "in_progress").length;
  const doneThisMonth = orders.filter((o) => o.status === "completed" && (o.created_at ?? "").slice(0, 7) === thisMonth).length;
  const monthCost = orders
    .filter((o) => (o.created_at ?? "").slice(0, 7) === thisMonth)
    .reduce((a, o) => a + (o.actual_cost_cents ?? o.estimated_cost_cents ?? 0), 0);

  const filtered = orders.filter((o) => {
    if (tab !== "all" && o.status !== tab) return false;
    if (veh && o.vehicle_id !== veh) return false;
    return true;
  });

  const vehName = (id: string) => vehMap.get(id)?.number ?? "—";

  function doReject(id: string) {
    const reason = window.prompt("驳回理由：");
    if (reason) reject.mutate({ id, reason });
  }
  function doComplete(o: MaintenanceOrder) {
    const v = window.prompt("录入实际金额 (USD)：", String((o.estimated_cost_cents ?? 0) / 100));
    if (v != null && !isNaN(Number(v))) complete.mutate({ id: o.id, cost: Math.round(Number(v) * 100) });
  }

  return (
    <PageShell
      title="🔧 维修工单 + 状态变更"
      subtitle="现阶段全部走老板审 · 维修厂手工输入（不建供应商档案）"
      actions={
        <Can perm="vehicles.maintenance.create">
          <Link to="/admin/vehicle-operations/maintenance/new" className="rounded-lg bg-blue-600 px-3.5 py-2 text-[13px] font-semibold text-white hover:bg-blue-700">
            + 新建维修工单
          </Link>
        </Can>
      }
    >
      <VehicleOpsTabs active="maintenance" />

      <MetricsRow
        items={[
          { label: "待审批", value: String(pending.length), hint: pending.length ? "🔴 老板需处理" : "良性" },
          { label: "维修中", value: String(inRepair) },
          { label: "本月完成", value: String(doneThisMonth) },
          { label: "本月维修费", value: usd(monthCost) },
        ]}
      />

      {/* 老板待审 */}
      {pending.length > 0 && (
        <Card title={`🔴 待老板审批维修工单（${pending.length}）· 现阶段全审`}>
          <div className="flex flex-col gap-2.5">
            {pending.map((o) => (
              <div key={o.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3.5">
                <div className="w-32 shrink-0">
                  <div className="text-[12px] font-bold text-amber-900">{o.order_number}</div>
                  <div className="text-[11px] text-amber-700">{o.created_at?.slice(0, 10)}</div>
                </div>
                <div className="w-24 shrink-0">
                  <div className="text-[13px] font-semibold">{vehName(o.vehicle_id)}</div>
                  <div className="text-[11px] text-slate-500">{vehMap.get(o.vehicle_id)?.model ?? ""}</div>
                </div>
                <span className={`shrink-0 rounded px-1.5 py-0.5 text-[11px] font-semibold ${TYPE_META[o.maintenance_type]?.cls ?? "bg-slate-100 text-slate-600"}`}>
                  {TYPE_META[o.maintenance_type]?.label ?? o.maintenance_type}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13px] font-medium text-slate-900">{o.description ?? "—"}</div>
                  <div className="truncate text-[11px] text-slate-500">维修厂：{o.vendor_name ?? "—"} · 预估 {usd(o.estimated_cost_cents)}</div>
                </div>
                <Can perm="vehicles.maintenance.approve"><div className="flex shrink-0 gap-2">
                  <button onClick={() => doReject(o.id)} disabled={reject.isPending} className="rounded-lg border border-slate-300 px-3 py-1.5 text-[12px] font-medium hover:bg-slate-50">驳回</button>
                  <button onClick={() => approve.mutate(o.id)} disabled={approve.isPending} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-emerald-700">批准 → 进维修</button>
                </div></Can>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* 列表 */}
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex gap-1">
            {STATUS_TABS.map((t) => {
              const n = t.key === "all" ? orders.length : orders.filter((o) => o.status === t.key).length;
              return (
                <button key={t.key} onClick={() => setTab(t.key)} className={`rounded-lg px-3 py-1.5 text-[13px] font-medium ${tab === t.key ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                  {t.label} {n}
                </button>
              );
            })}
          </div>
          <select value={veh} onChange={(e) => setVeh(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-1.5 text-[13px]">
            <option value="">全部车</option>
            {(vehQ.data ?? []).map((v) => <option key={v.id} value={v.id}>{v.vehicle_number}</option>)}
          </select>
        </div>

        <div className="mt-3 overflow-hidden rounded-lg border border-slate-200">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">工单号</th>
                <th className="px-3 py-2 text-left">车</th>
                <th className="px-3 py-2 text-left">类型</th>
                <th className="px-3 py-2 text-left">维修厂</th>
                <th className="px-3 py-2 text-right">金额</th>
                <th className="px-3 py-2 text-center">状态</th>
                <th className="px-3 py-2 text-right">日期</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400">暂无维修工单</td></tr>
              ) : filtered.map((o) => (
                <tr key={o.id} onClick={() => setSelected(o)} className="cursor-pointer border-t border-slate-100 hover:bg-blue-50">
                  <td className="px-3 py-2 font-medium text-slate-900">{o.order_number}</td>
                  <td className="px-3 py-2">{vehName(o.vehicle_id)}</td>
                  <td className="px-3 py-2">
                    <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${TYPE_META[o.maintenance_type]?.cls ?? "bg-slate-100 text-slate-600"}`}>
                      {TYPE_META[o.maintenance_type]?.label ?? o.maintenance_type}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-slate-600">{o.vendor_name ?? "—"}</td>
                  <td className="px-3 py-2 text-right font-semibold">{usd(o.actual_cost_cents ?? o.estimated_cost_cents)}</td>
                  <td className="px-3 py-2 text-center">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_META[o.status]?.cls ?? "bg-slate-100"}`}>{STATUS_META[o.status]?.label ?? o.status}</span>
                  </td>
                  <td className="px-3 py-2 text-right text-slate-500">{o.created_at?.slice(5, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* 维修工单详情 Drawer（点条目展开 · canvas 维修厂信息+凭证+状态）*/}
      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={() => setSelected(null)}>
          <div className="h-full w-full max-w-md overflow-y-auto bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 flex items-center justify-between border-b border-slate-200 bg-white px-5 py-4">
              <h2 className="text-[16px] font-bold text-slate-900">{selected.order_number}</h2>
              <button onClick={() => setSelected(null)} className="text-2xl leading-none text-slate-400 hover:text-slate-700">×</button>
            </div>
            <div className="flex flex-col gap-4 p-5">
              <div className="flex items-center gap-2">
                <span className={`rounded px-2 py-0.5 text-[12px] font-semibold ${TYPE_META[selected.maintenance_type]?.cls ?? "bg-slate-100 text-slate-600"}`}>
                  {TYPE_META[selected.maintenance_type]?.label ?? selected.maintenance_type}
                </span>
                <span className={`rounded-full px-2.5 py-0.5 text-[12px] font-semibold ${STATUS_META[selected.status]?.cls ?? "bg-slate-100"}`}>
                  {STATUS_META[selected.status]?.label ?? selected.status}
                </span>
              </div>
              <DetailRow label="车辆" value={`${vehName(selected.vehicle_id)} · ${vehMap.get(selected.vehicle_id)?.model ?? ""}`} />
              <DetailRow label="问题描述 / 原因" value={selected.description ?? "—"} />
              <DetailRow label="🏭 维修厂（手工输入·不建档）" value={selected.vendor_name ?? "—"} />
              <div className="grid grid-cols-2 gap-3">
                <DetailRow label="预估金额" value={usd(selected.estimated_cost_cents)} />
                <DetailRow label="实际金额" value={selected.actual_cost_cents != null ? usd(selected.actual_cost_cents) : "—"} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                {/* 🔴 2026-08-13：这栏原写「计划日期」是错的 —— 底层 scheduled_at 装的其实是
                    报修表单里那个必填的「报修时间」（唯一创建入口 VehicleOpsMaintenanceForm
                    的 reportedAt 字段）。「报修时间」是过去、「计划日期」是未来，显示成后者
                    会让车务以为已经排好送修日了。用户 08-13 裁决：按真实语义改名，
                    且**不需要「计划送修日期」这个概念**（流程就是报修即送修），不新增字段。 */}
                <DetailRow label="报修时间" value={selected.scheduled_at?.slice(0, 10) ?? "—"} />
                <DetailRow label="创建" value={selected.created_at?.slice(0, 10) ?? "—"} />
              </div>
              {selected.approved_at && <DetailRow label="📎 审批" value={`已审批 · ${selected.approved_at.slice(0, 10)}`} />}

              {/* 操作 */}
              {selected.status === "awaiting_approval" && canApproveMaint && (
                <div className="flex gap-2 border-t border-slate-200 pt-4">
                  <button onClick={() => doReject(selected.id)} disabled={reject.isPending} className="flex-1 rounded-lg border border-slate-300 px-4 py-2.5 text-[13px] font-medium hover:bg-slate-50">驳回</button>
                  <button onClick={() => approve.mutate(selected.id)} disabled={approve.isPending} className="flex-1 rounded-lg bg-emerald-600 px-4 py-2.5 text-[13px] font-semibold text-white hover:bg-emerald-700">批准 → 进维修</button>
                </div>
              )}
              {selected.status === "in_progress" && canUpdateMaint && (
                <div className="border-t border-slate-200 pt-4">
                  <button onClick={() => doComplete(selected)} disabled={complete.isPending} className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-[13px] font-semibold text-white hover:bg-blue-700">✓ 完工（录入实际金额）</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] font-medium text-slate-500">{label}</span>
      <span className="text-[14px] text-slate-900">{value}</span>
    </div>
  );
}
